import { useQuery } from '@apollo/client';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { CUSTOMER_STATUS_FROM_GQL } from '@/shared/graphql/enums';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

import { CUSTOMERS_QUERY } from './customers.gql';
import { CustomerFormDialog } from './CustomerFormDialog';

interface Customer {
  id: string;
  first_name: string;
  last_name: string | null;
  phone: string;
  email: string | null;
  rfc: string | null;
  status: string;
}

interface CustomersResponse {
  customers: {
    items: Customer[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

export function CustomersPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = useCrudList();
  const user = useAuthStore((s) => s.user);
  const can_write = user?.permissions.includes('customers.write') ?? false;
  const [creating, setCreating] = useState(false);

  const { data, loading, refetch } = useQuery<CustomersResponse>(CUSTOMERS_QUERY, {
    variables: {
      offset: list.offset,
      limit: list.limit,
      search: list.search || null,
      status_filter: list.status_filter,
    },
  });
  const rows = (data?.customers.items ?? []).map((row) => ({
    ...row,
    status:
      CUSTOMER_STATUS_FROM_GQL[row.status as keyof typeof CUSTOMER_STATUS_FROM_GQL] ??
      row.status,
  }));

  const columns: CrudColumn<Customer>[] = [
    {
      key: 'name',
      header_key: 'customers.name',
      render: (r) => (
        <div className="font-medium">
          {r.first_name}
          {r.last_name ? ` ${r.last_name}` : ''}
        </div>
      ),
    },
    {
      key: 'phone',
      header_key: 'customers.phone',
      width: 'w-40',
      render: (r) => <span className="font-mono text-xs">{r.phone}</span>,
    },
    {
      key: 'email',
      header_key: 'customers.email',
      render: (r) => r.email ?? '—',
    },
    {
      key: 'rfc',
      header_key: 'customers.rfc',
      width: 'w-36',
      render: (r) => (r.rfc ? <span className="font-mono text-xs">{r.rfc}</span> : '—'),
    },
    {
      key: 'status',
      header_key: 'common.status',
      align: 'center',
      width: 'w-32',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">{t('customers.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('customers.subtitle')}</p>
      </header>

      <CrudTable<Customer>
        rows={rows}
        columns={columns}
        loading={loading}
        page={data?.customers.page ?? { total: 0, offset: 0, limit: list.limit, has_more: false }}
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/app/customers/${r.id}`)}
        empty_label_key="customers.empty"
        toolbar={
          <CrudToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            placeholder_key="customers.search_placeholder"
            primary_action={
              can_write ? (
                <Button type="button" onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('customers.create')}</span>
                </Button>
              ) : undefined
            }
          />
        }
      />

      {creating ? (
        <CustomerFormDialog
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={async (id) => {
            setCreating(false);
            showToast({
              tone: 'success',
              title: t('alerts.create_success_title'),
              body: t('alerts.create_success_body'),
            });
            await refetch();
            navigate(`/app/customers/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}
